import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import globalSettingsBranches from '../../../../../stores/global-settings-branches';
import { Calendar } from 'primereact/calendar';
import { FilterExpanderWithListOfCheckboxes } from '../../../../../components/CustomExpander/componenets/filter-expander-with-list-of-checkboxes';
import { CustomExpander } from '../../../../../components/CustomExpander/CustomExpander';
import { Button } from 'primereact/button';
import { InstallmentsSideBarFilterType } from './installments-side-bar-filter.type';
import { PaymentInstallmentStatus } from '../../../../../api/types/reports/accountant/installment.type';

interface InstallmentsSideBarFilterProps {
  onSearch: (filters: InstallmentsSideBarFilterType) => void;
}

// Helper function to get display text for installment status
const getInstallmentStatusDisplayText = (
  status: PaymentInstallmentStatus,
): string => {
  switch (status) {
    case PaymentInstallmentStatus.PENDING:
      return 'ממתין לתשלום';
    case PaymentInstallmentStatus.PAID:
      return 'שולם';
    case PaymentInstallmentStatus.FAILED:
      return 'נכשל';
    case PaymentInstallmentStatus.CANCELLED:
      return 'בוטל';
    default:
      return 'לא ידוע';
  }
};

export const InstallmentsSideBarFilter = observer(
  ({ onSearch }: InstallmentsSideBarFilterProps) => {
    const [selectedBranchIds, setSelectedBranchIds] = useState<number[]>([]);
    const [includeUnassignedBranches, setIncludeUnassignedBranches] =
      useState<boolean>(true);
    const [selectedMonth, setSelectedMonth] = useState<Date | null>(new Date());
    const [selectedPaymentStatuses, setSelectedPaymentStatuses] = useState<
      PaymentInstallmentStatus[]
    >([]);

    const handleBranchToggle = (branchId: number) => {
      setSelectedBranchIds((prev) =>
        prev.includes(branchId)
          ? prev.filter((b) => b !== branchId)
          : [...prev, branchId],
      );
    };

    const styles = {
      container: {
        height: '100%',
        background: 'white',
        borderRadius: '8px',
        padding: '16px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        display: 'flex',
        flexDirection: 'column' as const,
      },
      headerRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
        gap: '8px',
      },
      content: {
        flex: 1,
        overflowY: 'auto' as const,
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '8px',
        paddingRight: '4px',
      },
      dateContainer: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '8px',
        padding: '8px',
      },
      calendarWrapper: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '8px',
      },
      calendarLabel: {
        fontSize: '0.9rem',
        color: '#666',
      },
      buttonContainer: {
        marginTop: '16px',
        padding: '8px',
        borderTop: '1px solid #e9ecef',
      },
    };

    const handleSearch = () => {
      const filters: InstallmentsSideBarFilterType = {
        branchIds: selectedBranchIds,
        includeUnassignedBranches,
        startMonth: selectedMonth,
        endMonth: selectedMonth,
        paymentStatuses: selectedPaymentStatuses,
      };
      onSearch(filters);
    };

    const handleClearAllFilters = () => {
      setSelectedBranchIds([]);
      setIncludeUnassignedBranches(true);
      setSelectedMonth(new Date());
      setSelectedPaymentStatuses([]);
    };

    const unassignedBranchOptions = [
      { key: 1, label: 'כלול תשלומים ללא שלוחה' },
    ];

    return (
      <div style={styles.container}>
        <div style={styles.headerRow}>
          <div
            style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#333' }}
          >
            סינונים
          </div>
          <Button
            icon="pi pi-filter-slash"
            text
            severity="secondary"
            tooltip={'נקה הכל'}
            onClick={handleClearAllFilters}
          />
        </div>
        <div style={styles.content}>
          <FilterExpanderWithListOfCheckboxes
            title="שלוחות"
            items={globalSettingsBranches.branches}
            selectedItems={selectedBranchIds}
            onItemToggle={handleBranchToggle}
            getItemLabel={(branch) => branch.branchName}
            getItemKey={(branch) => branch.id}
            defaultExpanded={true}
            searchPlaceholder="חפש שלוחה..."
          />

          <FilterExpanderWithListOfCheckboxes
            title="שלוחות ללא שיוך"
            items={unassignedBranchOptions}
            selectedItems={includeUnassignedBranches ? [1] : []}
            onItemToggle={() => setIncludeUnassignedBranches((prev) => !prev)}
            getItemLabel={(it) => it.label}
            getItemKey={(it) => it.key}
            defaultExpanded={true}
            searchPlaceholder="חפש..."
          />

          <CustomExpander defaultExpanded={true}>
            <div>בחירת חודש</div>
            <div style={styles.dateContainer}>
              <div style={styles.calendarWrapper}>
                <label style={styles.calendarLabel}>חודש לתשלום</label>
                <Calendar
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.value as Date)}
                  view="month"
                  dateFormat="mm/yy"
                  showIcon
                  iconPos="right"
                  style={{
                    height: '36px',
                    width: '100%',
                    direction: 'ltr',
                  }}
                  inputStyle={{ direction: 'rtl' }}
                  placeholder="בחר חודש"
                />
              </div>
            </div>
          </CustomExpander>

          <FilterExpanderWithListOfCheckboxes
            title="סטטוס תשלום"
            items={Object.values(PaymentInstallmentStatus)}
            selectedItems={selectedPaymentStatuses}
            onItemToggle={(status: PaymentInstallmentStatus) => {
              setSelectedPaymentStatuses((prev) =>
                prev.includes(status)
                  ? prev.filter((s) => s !== status)
                  : [...prev, status],
              );
            }}
            getItemLabel={(status) => getInstallmentStatusDisplayText(status)}
            getItemKey={(status) => status}
            searchPlaceholder="חפש סטטוס..."
          />
        </div>
        <div style={styles.buttonContainer}>
          <Button
            label="חפש"
            icon="pi pi-search"
            style={{ width: '100%' }}
            onClick={handleSearch}
          />
        </div>
      </div>
    );
  },
);
